import { useContext, useEffect, useState, } from "react";

import ConfirmationPopup from "../../../shared/components/confirmation-popup/confirmation-popup";
import ListPage from "../../../shared/components/list-page/list-page";
import { IColumn } from "../../../shared/components/normal-table/normal-table";
import Spinner from "../../../shared/components/spinner/spinner";
import { roleWrite, roleAdmin } from "../../../shared/constants/user-roles";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { getAllData, deleteMultiple, IListPageData } from "../../../shared/utils/api";
import { validateLoadingStatusNew } from "../../../shared/utils/utils";
import { datamodelEntityListUrl } from "../../constants/backend-constants";
import "./entity-list.scss";

const columns: IColumn[] = [
  {
    key: "name",
    displayName: "Name",
    dataType: {
      type: "text",
    }
  },
  {
    key: "total_rows",
    displayName: "Total rows",
    dataType: {
      type: "text",
    },
    nonSortable: true,
  },
  {
    key: "last_imported_date",
    displayName: "Last imported date",
    dataType: {
      type: "dateTime",
    },
    nonSortable: true,
  },
  {
    key: "archived",
    displayName: "Archived",
    model: "entity",
    dataType: {
      type: "text",
    },
  },
];

const EntityList = () => {
  const [data, setData] = useState<IListPageData | undefined>(undefined);
  const [unblockInput, setUnblockInput] = useState(false);
  const [sortedBy, setSortedBy] = useState("?order_by=name asc");
  const [currentOffset, setCurrentOffset] = useState(1);
  const authCtx = useContext(AuthContext);
  const { sendRequest: getSortBy, status: getSortByStatus, data: getSortByData, error: errorGetSortByData } = useHttp(getAllData, true, authCtx.token);
  const { sendRequest: deleteMultipleRequest, status: allStatusDelete, error: errorDelete } = useHttp(deleteMultiple, false, authCtx.token, "archived");
  const { sendRequest: getSortByPagination, status: getSortByPaginationStatus, data: getSortByPaginationData, error: getSortByPaginationError } = useHttp(getAllData, false, authCtx.token);
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const [idsToBeDeleted, setIdsToBeDeleted] = useState<string[]>([]);

  useEffect(() => {
    const rolesArray = authCtx.selectedTenant.roles;
    if (rolesArray.indexOf(roleWrite) !== -1 || rolesArray.indexOf(roleAdmin) !== -1) {
      setUnblockInput(true);
    }
  }, [authCtx.selectedTenant, authCtx.systemAdmin]);

  useEffect(() => {
    getSortBy("datamodel/entities", "?order_by=name asc&limit=50&offset=0");
    authCtx.setOnboardingPlatformToggleHandler();
  }, [getSortBy]);

  const onTableScrollAction = (offset: number) => {
    if (data && data.rows.length >= 50) {
      setCurrentOffset(offset);
      if (!getSortByPaginationData) {
        getSortByPagination("datamodel/entities", `${sortedBy}&limit=50&offset=${offset * 50}`);
      } else if (getSortByPaginationData.rows.length !== 0) {
        getSortByPagination("datamodel/entities", `?${sortedBy}&limit=50&offset=${offset * 50}`);
      }
    }
  };

  useEffect(() => {
    if (getSortByPaginationStatus === "completed" && !getSortByPaginationError) {
      data && setData({rows: [...data.rows, ...getSortByPaginationData.rows]});
    }
  }, [getSortByPaginationStatus]);

  useEffect(() => {
    if (getSortByStatus === "completed" && !errorGetSortByData) {
      setData(getSortByData);
    }
  }, [getSortByStatus]);

  useEffect(() => {
    if (allStatusDelete === "completed" && !errorDelete) {
      getSortBy("datamodel/entities", `${sortedBy}&limit=${currentOffset * 50}&offset=0`);
      authCtx.setOnboardingPlatformToggleHandler();
    }
  }, [allStatusDelete]);

  const handleOnDelete = (ids: string[]) => {
    const archivedItems = data?.rows.filter((row) => ids.includes(row.id) && row.archived);
    if (archivedItems?.length) {
      setConfirmationPopup(true);
      setIdsToBeDeleted(ids);
    } else {
      deleteMultipleRequest(`datamodel/entities${sortedBy}&limit=${currentOffset * 50}&offset=0`, { ids });
    }
  };

  const handleOnDeleteConfirmSubmit = () => {
    deleteMultipleRequest(`datamodel/entities${sortedBy}&limit=${currentOffset * 50}&offset=0`, { ids: idsToBeDeleted });
  };

  const sortBy = (sortingQuery: string) => {
    getSortBy("datamodel/entities", `${sortingQuery}&limit=${currentOffset * 50}&offset=0`);
    setSortedBy(sortingQuery);
  };

  return (
    <div className="entityList">
      {validateLoadingStatusNew([getSortByStatus, allStatusDelete, getSortByPaginationStatus]) && <Spinner />}
      <ConfirmationPopup
        confirmButtonText={"Delete"}
        text={"Some selected entities are archived. Are you sure you want to delete them?"}
        show={confirmationPopup}
        handlePopupClosed={() => setConfirmationPopup(false)}
        handleSaveClick={handleOnDeleteConfirmSubmit}
      />
      <ListPage
        title={"Entity types"}
        sortBy={sortBy}
        defaultSortCol={"name"}
        unblockInput={unblockInput}
        tooltipTxt={"Overview of all the Entities"}
        columns={columns}
        onTableScrollAction={onTableScrollAction}
        dataUrl={datamodelEntityListUrl}
        pageMode={"nonEditable"}
        handleOnDelete={handleOnDelete}
        fetchedData={data}
        allStatus={getSortByStatus}
      />
    </div>
  );
};

export default EntityList;
